/**
 * Created by rathompson on 10/26/21
 * File: ServiceAccountLayout
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo } from 'react'
import {
  Input,
  SubmitButton,
  Textarea,
} from '@panwds/react-form'
import { useFormContext } from 'react-hook-form'
import { InfoIcon } from '@panwds/icons'
import {
  Bold,
  Button,
  ModalCloseButton,
  ModalFooter,
  noop,
  Popover,
  CardBody,
  Body
} from '@panwds/react-ui'
import {
  BUTTON_CANCEL,
  BUTTON_NEXT,
  ERROR_INVALID_EMAIL,
  ERROR_ACCOUNT_INVALID,
  LABEL_SERVICE_ACCT_CONTACT,
  LABEL_SERVICE_ACCT_NAME,
  LABEL_DESCRIPTION,
  PLACEHOLDER_IDENTITY_ADDRESS,
  PLACEHOLDER_SERVICE_ACCOUNT_NAME,
  PLACEHOLDER_SERVICE_ACCOUNT_DESCRIPTION,
  LABEL_SERVICE_ACCT_EMAIL,
  BUTTON_REMOVE,
  LABEL_OPTIONAL,
  LABEL_SERVICE_ACCOUNT_RULES,
  LABEL_SERVICE_ACCOUNT_RULE_1,
  LABEL_SERVICE_ACCOUNT_RULE_2,
  LABEL_SERVICE_ACCOUNT_RULE_3, LABEL_SERVICE_ACCOUNT_RULE_4,
} from '../../../../../../constants/LabelConstants'
import {
  validateEmail,
  ACCOUNT_NAME_REGEX,
} from '../../../../../../../common/utils/Utils'
import {
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT,
  DATA_TEST_SERVICE_ACCOUNT_NAME,
  DATA_TEST_SERVICE_ACCOUNT_CONTACT,
  DATA_TEST_SERVICE_ACCOUNT_DESC,
  DATA_TEST_SERVICE_ACCOUNT_EMAIL,
} from '../../../../../../../common/constants/TestingDataIds'
import { getVarValue } from '../../../../../../../common/utils/Sparky'
import { FF_ROLES_ENABLED } from '../../../../../../../common/utils/FeatureFlags'
import ZIndex from '../../../../../../../common/components/zIndex/ZIndex'

const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)

/**
 *   Form fields specific to service accounts
 */
const ServiceAccountLayout = ({ onBackPressed, member, tenant, isEdit, onRemove, isLoading, length }) => {
  /**
   *     ========= Local State ===========
   */
  const { clearErrors, setValue, formState, trigger } = useFormContext()
  const { isValid } = formState

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    return () => {
      if (!member) {
        setValue('contact_email', '', { shouldDirty: true })
        setValue('name', '', { shouldDirty: true })
        setValue('description', '', { shouldDirty: true })
        clearErrors()
      }
    }
  }, [clearErrors, member, setValue])

  useEffect(() => {
    if (member) {
      setValue('name', member?.name, { shouldDirty: true })
      setValue('contact_email', member?.contact_email, { shouldDirty: true })
      setValue('description', member?.description, { shouldDirty: true })
    }
  }, [member, setValue])

  /**
   *     ========= Callback =============
   */
  const isValidAccountName = useCallback((name) => {
    const generatedAddress = `${name}@${tenant?.id}.iam.panserviceaccount.com`
    return validateEmail(generatedAddress)
  }, [tenant?.id])

  const fieldsDisabled = useMemo(() => {
    if (rolesEnabled) {
      return !!member && (!isEdit || member?.tsg_id !== tenant?.id)
    }
    else {
      return !!member && (!isEdit || member?.inherited_from !== tenant?.id)
    }
  }, [isEdit, member, tenant?.id])

  const removeDisabled = useMemo(() => {
    if (rolesEnabled) {
      return isLoading || !member?.inherited_from?.includes(tenant?.id) || length <= 1
    }
    else {
      return isLoading || member?.inherited_from !== tenant?.id
    }
  }, [isLoading, member?.inherited_from, tenant?.id, length])

  return (
    <div>
      <div className="tw-flex tw-items-center">
        <Bold appearance="semibold">{LABEL_SERVICE_ACCT_NAME}</Bold>
        <ZIndex>
          <Popover
            button={(
              <Button
                icon={<InfoIcon size='sm' className='tw-text-icon-gray' />}
                size='sm'
                appearance="clear"
                addClassName='tw-ml-1'
              />
            )}
            label="Account Name"
            showLabel={false}
            defaultPlacement="bottom-start"
          >
            <CardBody>
              <Bold appearance='semibold'>{LABEL_SERVICE_ACCOUNT_RULES}</Bold>
              <ul>
                <li><Body>{LABEL_SERVICE_ACCOUNT_RULE_1}</Body></li>
                <li><Body>{LABEL_SERVICE_ACCOUNT_RULE_2}</Body></li>
                <li><Body>{LABEL_SERVICE_ACCOUNT_RULE_3}</Body></li>
                <li><Body>{LABEL_SERVICE_ACCOUNT_RULE_4}<Bold>{'"(),:;<>@[\\]'}</Bold></Body></li>
              </ul>
            </CardBody>
          </Popover>
        </ZIndex>
      </div>
      <Input
        data-testid={DATA_TEST_SERVICE_ACCOUNT_NAME}
        label={LABEL_SERVICE_ACCT_NAME}
        showLabel={false}
        register={{
          required: !member,
          minLength: 1,
          maxLength: 255,
          validate: value => (ACCOUNT_NAME_REGEX.test(value) && isValidAccountName(value)) || ERROR_ACCOUNT_INVALID,
          setValueAs: value => value.trimEnd(),
          onChange: async () => await trigger('name'),
        }}
        addClassName="tw-mt-1"
        name="name"
        type="text"
        placeholder={PLACEHOLDER_SERVICE_ACCOUNT_NAME}
        required={!member}
        disabled={!!member}
      />
      {member?.identity_email && (
        <>
          <Bold appearance="semibold">{LABEL_SERVICE_ACCT_EMAIL}</Bold>
          <Input
            data-testid={DATA_TEST_SERVICE_ACCOUNT_EMAIL}
            label={LABEL_SERVICE_ACCT_EMAIL}
            showLabel={false}
            register={{
              required: false,
              value: member?.identity_email,
            }}
            addClassName="tw-mt-1"
            name="identity_email"
            type="text"
            required={false}
            disabled={true}
          />
        </>
      )}
      <div className="tw-flex tw-gap-1">
        <Bold appearance="semibold">{LABEL_SERVICE_ACCT_CONTACT}</Bold>
        <Bold appearance="semibold" addClassName="tw-text-gray-600">({LABEL_OPTIONAL})</Bold>
      </div>
      <Input
        data-testid={DATA_TEST_SERVICE_ACCOUNT_CONTACT}
        label={LABEL_SERVICE_ACCT_CONTACT}
        showLabel={false}
        addClassName="tw-mt-1"
        name="contact_email"
        type="email"
        register={{
          required: false,
          validate: value => (value ? validateEmail(value) || ERROR_INVALID_EMAIL : true),
          onChange: async () => await trigger('contact_email'),
        }}
        placeholder={PLACEHOLDER_IDENTITY_ADDRESS}
        disabled={fieldsDisabled}
        required={false}
      />
      <div className="tw-flex tw-gap-1">
        <Bold appearance="semibold">{LABEL_DESCRIPTION}</Bold>
        <Bold appearance="semibold" addClassName="tw-text-gray-600">({LABEL_OPTIONAL})</Bold>
      </div>
      <Textarea
        data-testid={DATA_TEST_SERVICE_ACCOUNT_DESC}
        rowsMin={3}
        label={LABEL_DESCRIPTION}
        register={{
          required: false,
          onChange: async () => await trigger('description'),
        }}
        showLabel={false}
        addClassName="tw-mt-1"
        name="description"
        placeholder={PLACEHOLDER_SERVICE_ACCOUNT_DESCRIPTION}
        disabled={fieldsDisabled}
      />
      <ModalFooter style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '43px' }}>
        {isEdit && (
          <ModalCloseButton
            appearance="secondary-destructive"
            onClick={onRemove}
            disabled={removeDisabled}
          >{BUTTON_REMOVE}</ModalCloseButton>
        )}
        <Button
          data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
          onClick={onBackPressed}
          appearance="secondary"
        >{BUTTON_CANCEL}</Button>
        <SubmitButton
          data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
          appearance="primary"
          disabled={isLoading || !isValid}
        >{BUTTON_NEXT}</SubmitButton>
      </ModalFooter>
    </div>
  )
}

ServiceAccountLayout.defaultProps = {
  onBackPressed: noop,
}

export default ServiceAccountLayout
